import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';

import downloadCsvIcon from '../../../assets/images/download-csv-icon.svg';
import filetypeTemplateIcon from '../../../assets/images/Filetype-Template.svg';
import closeIcon from '../../../assets/images/input-close-icon.svg';
import uploadIconContainer from '../../../assets/images/Upload-Icon-Container.svg';
import uploadIcon from '../../../assets/images/upload-icon.svg';
import {
  MAX_FILE_SIZE_TEXT,
  SUPPORTED_FORMATS_TEXT,
} from '../../../utils/csv/constants';
import {
  formatFileSize,
  getFileInputAccept,
  validateCSVFile,
} from '../../../utils/csv/fileUtils';
import Spinner from '../../Spinner/Spinner';

/**
 * CSV Upload Modal Component
 *
 * Displays a modal for uploading CSV files with drag & drop functionality.
 * Validates file format (CSV, XLS, XLSX) and size (max 25MB).
 *
 * @param {Object} props
 * @param {boolean} props.isOpen - Whether the modal is open
 * @param {Function} props.onClose - Callback when modal is closed
 * @param {Function} props.onDownloadTemplate - Callback when download template is clicked
 * @param {Function} props.onUpload - Callback when file is uploaded (receives File object)
 * @param {boolean} [props.isValidating] - Whether CSV is being validated
 * @param {string} [props.uploadError] - Error message from upload process
 * @param {Function} [props.onFileSelect] - Callback when a new file is selected (to clear errors)
 */
const CSVUploadModal = ({
  isOpen,
  onClose,
  onDownloadTemplate,
  onUpload,
  isValidating = false,
  uploadError = '',
  onFileSelect,
}) => {
  const [selectedFile, setSelectedFile] = useState(
    /** @type {File | null} */ (null),
  );
  const [error, setError] = useState('');
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef(/** @type {HTMLInputElement | null} */ (null));
  const prevIsOpenRef = useRef(isOpen);

  // Clear local error when uploadError prop changes (from parent)
  useEffect(() => {
    if (uploadError) {
      setError('');
      // Keep the file selected so error can be displayed in the file card
    }
  }, [uploadError]);

  // Clear file and error when modal closes
  useEffect(() => {
    if (prevIsOpenRef.current && !isOpen) {
      setSelectedFile(null);
      setError('');
      setIsDragging(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
    prevIsOpenRef.current = isOpen;
  }, [isOpen]);

  /**
   * Handles file selection
   * @param {File} file
   */
  const handleFileSelect = useCallback(
    (file) => {
      setError('');
      // Notify parent to clear upload error when a new file is selected
      if (onFileSelect) {
        onFileSelect();
      }
      const validation = validateCSVFile(file);

      if (validation.isValid) {
        setSelectedFile(file);
      } else {
        setError(validation.error || 'Invalid file');
        setSelectedFile(null);
      }
    },
    [onFileSelect],
  );

  /**
   * Handles file input change
   */
  const handleFileInputChange = useCallback(
    (e) => {
      const file = e.target.files?.[0];
      if (file) {
        handleFileSelect(file);
      }
    },
    [handleFileSelect],
  );

  /**
   * Handles drag and drop
   */
  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback(
    (e) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(false);

      const file = e.dataTransfer.files?.[0];
      if (file) {
        handleFileSelect(file);
      }
    },
    [handleFileSelect],
  );

  /**
   * Handles file upload
   */
  const handleUpload = useCallback(() => {
    if (!selectedFile) {
      setError('Please select a file to upload.');
      return;
    }

    const validation = validateCSVFile(selectedFile);
    if (!validation.isValid) {
      setError(validation.error || 'Invalid file');
      return;
    }

    // Don't clear the file immediately - let the parent handle validation
    // The file will be cleared only if upload is successful (modal closes)
    // If there's an error, we need to keep the file to display the error
    onUpload(selectedFile);
    // Clear local error only - keep file selected for error display
    setError('');
  }, [selectedFile, onUpload]);

  /**
   * Handles click on "Choose file" link
   */
  const handleChooseFileClick = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  /**
   * Resets state when modal closes
   */
  const handleClose = useCallback(() => {
    setSelectedFile(null);
    setError('');
    setIsDragging(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    onClose();
  }, [onClose]);

  if (!isOpen) {
    return null;
  }

  return (
    <>
      {/* Modal backdrop */}
      <div
        className="fixed inset-0 bg-black/60 z-40"
        onClick={handleClose}
        aria-hidden="true"
      />

      {/* Validation spinner overlay */}
      {isValidating && <Spinner />}

      {/* Modal */}
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 lg:p-4">
        <div
          className="w-full max-w-[375px] lg:max-w-[684px] flex flex-col gap-5 lg:gap-7 bg-white rounded-lg p-6"
          role="dialog"
          aria-modal="true"
          aria-labelledby="csv-upload-modal-title"
          onClick={(e) => e.stopPropagation()}
          onKeyDown={(e) => {
            if (e.key === 'Escape') {
              handleClose();
            }
          }}
        >
          {/* Header */}
          <div className="flex flex-col gap-3 relative">
            <div className="flex items-start justify-end">
              <button
                type="button"
                onClick={handleClose}
                className="w-4 h-4 flex items-center justify-center cursor-pointer transition-opacity duration-300 hover:opacity-80"
                aria-label="Close modal"
              >
                <img src={closeIcon} alt="Close icon" />
              </button>
            </div>
            <div className="flex flex-col gap-1">
              <div className="flex items-center justify-between">
                <h2
                  id="csv-upload-modal-title"
                  className="text-2xl font-semibold text-gray-90"
                >
                  Upload Roster CSV
                </h2>
                {/* Desktop: Download button next to title */}
                <button
                  type="button"
                  onClick={onDownloadTemplate}
                  className="hidden lg:flex items-center gap-2 text-blue-30 font-semibold text-base hover:opacity-80 transition-opacity"
                  aria-label="Download CSV template"
                >
                  <img
                    src={downloadCsvIcon}
                    alt=""
                    className="w-5 h-5 shrink-0"
                  />
                  <span>Download CSV template</span>
                </button>
              </div>
              {/* Mobile: Show description text */}
              <p className="text-sm font-light text-gray-90 lg:hidden">
                Download the template before uploading
              </p>
              {/* Desktop: Show supported formats */}
              <p className="text-sm font-light text-gray-90 hidden lg:block">
                Supported formats: {SUPPORTED_FORMATS_TEXT}
              </p>
            </div>
            {/* Mobile: Download template button below title */}
            <button
              type="button"
              onClick={onDownloadTemplate}
              className="flex items-center gap-2 text-blue-30 font-semibold text-base hover:opacity-80 transition-opacity lg:hidden"
              aria-label="Download CSV template"
            >
              <img src={downloadCsvIcon} alt="" className="w-5 h-5 shrink-0" />
              <span>Download CSV template</span>
            </button>
          </div>

          {/* Upload area */}
          <div className="flex flex-col items-start gap-4">
            {/* biome-ignore lint/a11y/useSemanticElements: Div is required for drag-and-drop functionality */}
            <div
              className={`w-full h-[200px] flex flex-col gap-4 items-center justify-center px-4 py-10 rounded border-2 border-dashed transition-colors ${
                isDragging
                  ? 'border-blue-50 bg-blue-05'
                  : 'border-deep-blue-70 bg-white'
              }`}
              role="button"
              tabIndex={0}
              aria-label="Drag and drop CSV file area or click to choose file"
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              onClick={handleChooseFileClick}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  handleChooseFileClick();
                }
              }}
            >
              {/* Upload icon */}
              <div className="w-12 h-12 flex items-center justify-center">
                <img
                  src={isDragging ? uploadIconContainer : uploadIcon}
                  alt="Upload"
                  className="w-10 h-10"
                />
              </div>

              {/* Drag and drop text */}
              <div className="flex flex-col gap-1 items-center text-sm font-light text-gray-90">
                <p className="text-center">
                  Drag and Drop CSV here or{' '}
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleChooseFileClick();
                    }}
                    className="text-blue-30 font-semibold hover:underline"
                  >
                    Choose file
                  </button>
                </p>
                {/* Mobile: Show supported formats inside drag area */}
                <p className="text-sm font-light text-gray-90 lg:hidden">
                  Supported formats: {SUPPORTED_FORMATS_TEXT}
                </p>
                <p className="text-sm font-light text-gray-90">
                  Maximum size: {MAX_FILE_SIZE_TEXT}
                </p>
              </div>

              {/* Hidden file input */}
              <input
                ref={fileInputRef}
                type="file"
                accept={getFileInputAccept()}
                onChange={handleFileInputChange}
                className="hidden"
                aria-label="Select CSV file"
              />
            </div>

            {/* Selected file display */}
            {selectedFile && (
              <div
                className={`w-full p-3 bg-white rounded border flex items-center gap-5 ${
                  uploadError ? 'border-coral-96' : 'border-gray-20'
                }`}
              >
                <div className="flex items-center gap-2 flex-1 min-w-0">
                  {/* CSV file icon */}
                  <img
                    src={filetypeTemplateIcon}
                    alt="CSV file"
                    className="w-[34px] h-10 flex-shrink-0"
                  />
                  <div className="flex flex-col gap-1 flex-1 min-w-0">
                    <span className="text-sm font-semibold text-gray-90 truncate">
                      {selectedFile.name}
                    </span>
                    <span className="text-xs font-light text-gray-60">
                      {formatFileSize(selectedFile.size)}
                    </span>
                    {/* Error message inside file card */}
                    {uploadError && (
                      <span className="text-sm font-semibold text-coral-96">
                        {uploadError}
                      </span>
                    )}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => {
                    setSelectedFile(null);
                    setError('');
                    // Notify parent to clear upload error when file is removed
                    if (onFileSelect) {
                      onFileSelect();
                    }
                    if (fileInputRef.current) {
                      fileInputRef.current.value = '';
                    }
                  }}
                  className="w-7 h-7 rounded-full bg-gray-20 flex items-center justify-center flex-shrink-0 hover:bg-gray-30 transition-colors"
                  aria-label="Remove file"
                >
                  <img src={closeIcon} alt="Remove" className="w-3 h-3" />
                </button>
              </div>
            )}

            {/* Local error message (for file validation errors, not upload errors) */}
            {error && !uploadError && (
              <div className="mt-4 w-full p-3 bg-coral-05 rounded border border-coral-96">
                <p className="text-sm text-coral-96 font-semibold">{error}</p>
              </div>
            )}
          </div>

          {/* Footer with Upload button */}
          <div className="flex items-center justify-end pt-8 border-t border-gray-30">
            <button
              type="button"
              onClick={handleUpload}
              disabled={
                !selectedFile || !!error || !!uploadError || isValidating
              }
              className={`px-8 py-3 rounded-full font-extrabold text-sm tracking-wide transition-colors ${
                selectedFile && !error && !uploadError && !isValidating
                  ? 'bg-deep-blue-30 text-white hover:bg-deep-blue-50 cursor-pointer'
                  : 'bg-gray-20 text-gray-60 cursor-not-allowed'
              }`}
            >
              Upload CSV
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

CSVUploadModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onDownloadTemplate: PropTypes.func.isRequired,
  onUpload: PropTypes.func.isRequired,
  isValidating: PropTypes.bool,
  uploadError: PropTypes.string,
  onFileSelect: PropTypes.func,
};

export default CSVUploadModal;
